<script setup lang="ts">
import { DEFAULT_ANYWAY_PARTNER_BG_COLOR } from '~kui/constants/anyway';

interface Props {
  partnerBgColor?: string;
  iconUrl?: string | null;
  completed?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  incompleteBackgroundVariant?: 'dark' | 'light';
}

const props = withDefaults(defineProps<Props>(), {
  iconUrl: null,
  completed: false,
  size: 'md',
  partnerBgColor: DEFAULT_ANYWAY_PARTNER_BG_COLOR,
  incompleteBackgroundVariant: 'light',
});

const showFlash = ref(false);
let flashTimer: ReturnType<typeof setTimeout> | null = null;

watch(() => props.completed, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    showFlash.value = true;
    flashTimer = setTimeout(() => {
      showFlash.value = false;
    }, 500);
  }
});

onUnmounted(() => {
  if (flashTimer !== null) {
    clearTimeout(flashTimer);
  }
});

const sizeStyles: Record<string, { width: string; height: string; aspectRatio: string }> = {
  xs: { width: '24px', height: '24px', aspectRatio: '24/24' },
  sm: { width: '27px', height: '27px', aspectRatio: '27/27' },
  md: { width: '30px', height: '30px', aspectRatio: '30/30' },
  lg: { width: '36px', height: '36px', aspectRatio: '36/36' },
};

const classes = computed(() => {
  return twMerge(
    cva('absolute inset-0 flex items-center justify-center overflow-hidden', {
      variants: {
        isCompleted: {
          true: 'bg-white',
          false: '',
        },
      },
    })({
      isCompleted: props.completed,
    }),
  );
})

const miniBadgeClasses = computed(() => {
  return twMerge(
    cva('aspect-square m-0', {
      variants: {
        size: {
          xs: 'h-3.5',
          sm: 'h-4',
          md: 'h-4.5',
          lg: 'h-5.5',
        },
      },
    })({
      size: props.size,
    }),
  );
})

const miniBadgeInternalClasses = computed(() => {
  return twMerge(
    cva('absolute inset-0', {
      variants: {
        incompleteBackgroundVariant: {
          dark: 'bg-primary/30',
          light: 'bg-card/50',
        },
      },
    })({
      incompleteBackgroundVariant: props.incompleteBackgroundVariant,
    }),
  );
})

const polygonClipPath = 'polygon(50% 7.5%, 85% 25%, 85% 73%, 49% 93%, 15% 73%, 15% 27%)';
</script>

<template>
  <div class="relative" :style="sizeStyles[size]">
    <!-- Icon fill (visible when completed) -->
    <div
      :class="classes"
      :style="{ clipPath: polygonClipPath }"
    >
      <Transition name="badge-reveal" mode="out-in">
        <!-- make it as baby badge -->
        <div v-if="completed" class="scale-[1.05] drop-shadow-xs drop-shadow-border/60">
          <div
            :class="miniBadgeClasses"
            :style="{
              backgroundColor: partnerBgColor,
              clipPath: polygonClipPath,
            }"
          />
        </div>

        <div
          v-else
          key="empty"
          class="relative size-full"
        >
          <!-- base color -->
          <div :class="miniBadgeInternalClasses" />

          <!-- top-left light catch -->
          <div
            class="absolute inset-0"
            style="background: radial-gradient(ellipse at 25% 20%, rgba(255,255,255,0.55) 0%, transparent 55%)"
          />

          <!-- bottom-right light catch -->
          <div
            class="absolute inset-0"
            style="background: radial-gradient(ellipse at 78% 82%, rgba(255,255,255,0.25) 0%, transparent 45%)"
          />

          <!-- subtle darkening on edges for depth -->
          <div
            class="absolute inset-0"
            style="background: radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.12) 100%)"
          />
        </div>
      </Transition>
    </div>

    <!-- Chrome shield frame (always on top) -->
    <NuxtImg
      v-if="completed"
      src="/images/kui/unified/activity-badge-shield.webp"
      alt=""
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 size-full object-fill"
    />

    <!-- Flash overlay on reveal -->
    <div
      v-if="showFlash"
      class="badge-flash pointer-events-none absolute inset-0 rounded-full bg-white"
    />
  </div>
</template>

<style scoped>
.badge-reveal-enter-active {
  animation: badge-pop-in 0.4s ease-out 0.15s both;
}

.badge-reveal-leave-active {
  transition: opacity 0.1s ease;
}

.badge-reveal-leave-to {
  opacity: 0;
}

@keyframes badge-pop-in {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  60% {
    opacity: 1;
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}

.badge-flash {
  animation: flash-burst 0.45s ease-out forwards;
  z-index: 10;
}

@keyframes flash-burst {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  20% {
    opacity: 0.9;
    transform: scale(1.4);
  }
  100% {
    opacity: 0;
    transform: scale(2);
  }
}
</style>
